/**
 * /settings — application settings.
 *
 * Monitor-only build. Sections:
 *
 *   0. **Apex connection** — controller IP/credentials + live status
 *      badge (stored on the Pi; hot-restarts the poller on save).
 *   1. **Storage** — time-series storage stats + retention.
 *   2. **Appearance** / **Kiosk** — display preferences.
 *   3. **Backup & Restore** — full user-data backup/restore.
 *   4. **System** — app version + agent host platform (best-effort fetch;
 *      degrades gracefully when the agent is offline).
 *
 * Wrapped in `SettingsLayout`, which renders the sticky scroll-spy TOC.
 */
import { useEffect, useState } from 'react';

import { Button } from '../components/Button';
import { Card, CardBody, CardHeader, CardTitle } from '../components/Card';
import { agentFetch, readAccessKey, writeAccessKey } from '../lib/agent-fetch';
import { APP_VERSION } from '../lib/appVersion';
import {
  loadPublicEnv,
  readPiBaseUrlOverride,
  writePiBaseUrlOverride,
} from '../lib/env';
import { ApexConnectionSection } from './settings/ApexConnectionSection';
import { BackupRestoreSection } from './settings/BackupRestoreSection';
import { SettingsAppearanceSection } from './settings/SettingsAppearanceSection';
import { PasscodeSection } from './settings/PasscodeSection';
import { SettingsKioskSection } from './settings/SettingsKioskSection';
import { SettingsLayout, type TocSection } from './settings/SettingsLayout';
import { StorageSection } from './settings/StorageSection';

/** TOC entries for the surviving settings sections. */
const TOC_SECTIONS: readonly TocSection[] = [
  { id: 'settings-apex', label: 'Apex connection', group: 'Connection' },
  { id: 'settings-passcode', label: 'Passcode', group: 'Connection' },
  { id: 'settings-storage', label: 'Storage', group: 'Data' },
  { id: 'settings-backup', label: 'Backup & Restore', group: 'Data' },
  { id: 'settings-appearance', label: 'Appearance', group: 'Display' },
  { id: 'settings-kiosk', label: 'Kiosk', group: 'Display' },
  { id: 'settings-system', label: 'System', group: 'System' },
];

/** Narrow the `/api/system/platform` response body. */
function narrowPlatform(body: unknown): 'pi' | 'laptop' | null {
  if (body === null || typeof body !== 'object') return null;
  const p = (body as Record<string, unknown>).platform;
  return p === 'pi' || p === 'laptop' ? p : null;
}

export function SettingsPage() {
  // System section: agent host platform. Best-effort — the row is hidden
  // when the agent is offline or returns something unexpected.
  const [platform, setPlatform] = useState<'pi' | 'laptop' | null>(null);

  // Pi base URL override (advanced): persists to localStorage and takes
  // effect on the next page load.
  const [piUrl, setPiUrl] = useState(() => readPiBaseUrlOverride() ?? '');
  const [piUrlSaved, setPiUrlSaved] = useState(false);

  function savePiUrl() {
    writePiBaseUrlOverride(piUrl.length > 0 ? piUrl : null);
    setPiUrl(readPiBaseUrlOverride() ?? '');
    setPiUrlSaved(true);
  }

  // Access passcode for this device (public-access build). Stored in
  // localStorage; the Pi checks it on every request.
  const [passcode, setPasscode] = useState(() => readAccessKey() ?? '');
  const [passcodeSaved, setPasscodeSaved] = useState(false);

  function savePasscode() {
    writeAccessKey(passcode.length > 0 ? passcode : null);
    setPasscode(readAccessKey() ?? '');
    setPasscodeSaved(true);
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const base = loadPublicEnv().agentDevUrl.replace(/\/+$/, '');
        const res = await agentFetch(`${base}/api/system/platform`);
        if (!res.ok) return;
        const body: unknown = await res.json();
        if (cancelled) return;
        setPlatform(narrowPlatform(body));
      } catch {
        // Agent offline — leave platform hidden.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      className="min-h-screen px-6 py-8 max-w-5xl mx-auto"
      data-testid="settings-page"
    >
      <SettingsLayout sections={TOC_SECTIONS}>
        <div className="max-w-3xl">
          <section id="settings-apex">
            <ApexConnectionSection />
          </section>

          <section id="settings-passcode">
            <PasscodeSection />
          </section>

          <section id="settings-storage">
            <StorageSection />
          </section>

          <section id="settings-appearance">
            <SettingsAppearanceSection />
          </section>

          <section id="settings-kiosk">
            <SettingsKioskSection />
          </section>

          <section id="settings-backup">
            <BackupRestoreSection />
          </section>

          <section id="settings-system">
            <Card className="mt-6" data-testid="settings-system-section">
              <CardHeader>
                <CardTitle>System</CardTitle>
              </CardHeader>
              <CardBody>
                <dl className="text-sm text-reef-200 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  <dt className="text-reef-400">App version</dt>
                  <dd data-testid="settings-app-version">{APP_VERSION}</dd>
                  {platform !== null ? (
                    <>
                      <dt className="text-reef-400">Platform</dt>
                      <dd data-testid="settings-platform">
                        {platform === 'pi' ? 'Raspberry Pi' : 'Laptop'}
                      </dd>
                    </>
                  ) : null}
                </dl>

                <div className="mt-5 border-t border-reef-700/50 pt-4">
                  <p className="text-xs text-reef-300 mb-2">
                    <strong>Pi address override</strong> (advanced). Normally
                    the app finds your Pi automatically. Set this only if you
                    host the app elsewhere and need to point it at a different
                    Pi address (e.g.{' '}
                    <code>https://reefapp-pi.tail7e0fe8.ts.net</code>). Leave
                    blank for automatic. Takes effect after a page reload.
                  </p>
                  <div className="flex items-center gap-2 max-w-xl">
                    <input
                      type="text"
                      value={piUrl}
                      onChange={(e) => {
                        setPiUrl(e.target.value);
                        setPiUrlSaved(false);
                      }}
                      placeholder="automatic"
                      className="flex-1 rounded-md border border-reef-700/60 bg-reef-900/60 px-2.5 py-1.5 text-sm text-reef-100"
                      data-testid="settings-pi-url-input"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={savePiUrl}
                      data-testid="settings-pi-url-save"
                    >
                      Save
                    </Button>
                  </div>
                  {piUrlSaved ? (
                    <p
                      className="mt-2 text-xs text-emerald-300"
                      data-testid="settings-pi-url-saved"
                    >
                      Saved — reload the page to apply.
                    </p>
                  ) : null}
                </div>

                <div className="mt-5 border-t border-reef-700/50 pt-4">
                  <p className="text-xs text-reef-300 mb-2">
                    <strong>Access passcode</strong> for this device. The Pi
                    requires it on every request; you normally enter it once
                    via the unlock prompt. Update it here if the passcode
                    changes. Takes effect after a page reload.
                  </p>
                  <div className="flex items-center gap-2 max-w-xl">
                    <input
                      type="password"
                      value={passcode}
                      onChange={(e) => {
                        setPasscode(e.target.value);
                        setPasscodeSaved(false);
                      }}
                      className="flex-1 rounded-md border border-reef-700/60 bg-reef-900/60 px-2.5 py-1.5 text-sm text-reef-100"
                      data-testid="settings-passcode-input"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={savePasscode}
                      data-testid="settings-passcode-save"
                    >
                      Save
                    </Button>
                  </div>
                  {passcodeSaved ? (
                    <p
                      className="mt-2 text-xs text-emerald-300"
                      data-testid="settings-passcode-saved"
                    >
                      Saved — reload the page to apply.
                    </p>
                  ) : null}
                </div>
              </CardBody>
            </Card>
          </section>
        </div>
      </SettingsLayout>
    </div>
  );
}
